import React from 'react'
import { useForm, router } from '@inertiajs/react'
import { PageProps, Survey, Question as QuestionType } from '@/types'
import QuestionForm from '@/components/QuestionForm'

// A temporary ID for new questions that are not yet saved.
// Rails expects no ID for new records in nested attributes.
// We use a large number to distinguish from real IDs from the database.
const isNewQuestion = (q: { id?: number }) => !q.id

// Define an interface for our form data to get type safety.
type QuestionAttribute = Omit<QuestionType, 'id'> & { id?: number; _destroy?: boolean; position: number; is_csat: boolean }

interface SurveyFormData {
  name: string
  description: string
  hours: number
  minutes: number
  questions_attributes: QuestionAttribute[]
}

interface SurveyEditProps extends PageProps {
  survey: Survey
  is_master?: boolean
  survey_name_translated?: string
  is_accessibility_survey?: boolean
  errors?: Record<string, string>
}

const Edit: React.FC<SurveyEditProps> = ({ survey, is_master = false, survey_name_translated, is_accessibility_survey = false, errors = {} }) => {
  const { data, setData, put, processing } = useForm({
    name: survey.name || '',
    description: survey.description || 'Ayúdanos a seguir mejorando...',
    hours: survey.hours || 0,
    minutes: survey.minutes || 2,
    questions_attributes: survey.questions.map((q: QuestionType, index: number) => ({
      ...q,
      position: index,
      is_csat: q.is_csat || false,
      _destroy: false
    }) as QuestionAttribute)
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const questionsToSubmit = data.questions_attributes
      .map((q, index) => ({ ...q, position: index }))
      .filter(q => q.id || !q._destroy) // Filter out new questions marked for deletion

    const finalData = { ...data, questions_attributes: questionsToSubmit }

    const url = is_master ? `/admin/master_surveys/${survey.id}` : `/admin/surveys/${survey.id}`
    const redirectUrl = is_master ? `/admin/master_surveys` : `/admin/surveys/${survey.id}`

    put(url, {
      data: finalData as any,
      onSuccess: () => router.get(redirectUrl)
    } as any)
  }

  const handleQuestionChange = (originalIndex: number, updatedQuestion: Partial<QuestionType>) => {
    const newQuestions = [...data.questions_attributes]
    newQuestions[originalIndex] = { ...newQuestions[originalIndex], ...updatedQuestion }
    setData('questions_attributes', newQuestions)
  }

  const handleCsatChange = (questionId: number | undefined, isCsat: boolean) => {
    if (isCsat) {
      // Si se activa CSAT, desactivar todos los otros CSAT
      const newQuestions = data.questions_attributes.map(q => ({
        ...q,
        is_csat: q.id === questionId ? true : false
      }))
      setData('questions_attributes', newQuestions)
    }
  }

  const addQuestion = () => {
    const newQuestion: QuestionAttribute = {
      body: '',
      kind: 'multiple_option',
      required: false,
      is_csat: false,
      payload: { options: [{ text: '', position: 1 }] },
      position: data.questions_attributes.length,
      _destroy: false
    }
    setData('questions_attributes', [...data.questions_attributes, newQuestion])
  }

  const deleteQuestion = (originalIndex: number) => {
    const questionToDelete = data.questions_attributes[originalIndex]
    const newQuestions = [...data.questions_attributes]

    if (isNewQuestion(questionToDelete)) {
      // If it's a new question, remove it from the array.
      setData(
        'questions_attributes',
        newQuestions.filter((_, i) => i !== originalIndex)
      )
    } else {
      // If it's an existing question, mark it for destruction.
      newQuestions[originalIndex] = { ...questionToDelete, _destroy: true }
      setData('questions_attributes', newQuestions)
    }
  }

  const moveQuestion = (originalIndex: number, direction: 'up' | 'down') => {
    const newQuestions = [...data.questions_attributes]
    let targetIndex = -1

    if (direction === 'up') {
      for (let i = originalIndex - 1; i >= 0; i--) {
        if (!newQuestions[i]._destroy) {
          targetIndex = i
          break
        }
      }
    } else {
      for (let i = originalIndex + 1; i < newQuestions.length; i++) {
        if (!newQuestions[i]._destroy) {
          targetIndex = i
          break
        }
      }
    }

    if (targetIndex !== -1) {
      ;[newQuestions[originalIndex], newQuestions[targetIndex]] = [
        newQuestions[targetIndex],
        newQuestions[originalIndex]
      ]
      setData('questions_attributes', newQuestions)
    }
  }

  const visibleQuestions = data.questions_attributes
    .map((q, index) => ({ ...q, originalIndex: index }))
    .filter(q => !q._destroy)

  return (
    <div className="container mx-auto mt-8 px-4">
      <h1 className="text-2xl font-bold mb-2">{survey_name_translated || survey.name}</h1>
      <p className="text-gray-600 mb-8">Puedes editar o agregar nuevas preguntas a los 2 tipos de encuestas.</p>

      <form onSubmit={handleSubmit}>
        <section className="bg-blue-100 rounded-lg p-6 mb-8 mr-32">
          <div className="flex-col">
            <h2 className="text-xl font-bold mb-6">1. Información general</h2>
            <div className="space-y-6">
              <div className="field">
                <label className="block text-sm mb-1 font-bold text-gray-800">Nombre de la encuesta</label>
                <p className="text-sm text-gray-500 mb-2">
                  Es el nombre que tendrá el formulario. Este no podrá ser editado por el funcionario público.
                </p>
                <input
                  type="text"
                  value={survey_name_translated || data.name}
                  onChange={e => setData('name', e.target.value)}
                  className="w-full p-2 border-2 border-black rounded-lg bg-gray-200 cursor-not-allowed max-w-xl"
                  disabled
                />
              </div>

              <div className="flex">
                <div className="field mr-8">
                  <label className="block text-sm mb-1 font-bold text-gray-800">Horas</label>
                  <input
                    type="number"
                    value={data.hours}
                    onChange={e => setData('hours', parseInt(e.target.value, 10))}
                    className="w-full p-2 border-2 border-black rounded-lg max-w-32"
                  />
                </div>
                <div className="field">
                  <label className="block text-sm mb-1 font-bold text-gray-800">Minutos</label>
                  <input
                    type="number"
                    value={data.minutes}
                    onChange={e => setData('minutes', parseInt(e.target.value, 10))}
                    className="w-full p-2 border-2 border-black rounded-lg max-w-32"
                  />
                </div>
              </div>

              <div className="field">
                <label className="block text-sm mb-1 font-bold text-gray-800">Descripción general</label>
                <p className="text-sm text-gray-500 mb-2">Texto que presenta el formulario al ciudadano/a.</p>
                <textarea
                  value={data.description}
                  onChange={e => setData('description', e.target.value)}
                  className="w-full p-2 border-2 border-black rounded-lg h-24 bg-white"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">2. Preguntas de la encuesta ({visibleQuestions.length})</h2>
          </div>
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-red-800 font-medium mb-2">Errores de validación:</h3>
              <ul className="text-red-700 text-sm space-y-1">
                {Object.entries(errors).map(([key, message]) => (
                  <li key={key}>• {message}</li>
                ))}
              </ul>
            </div>
          )}
          <div id="questions" className="space-y-4">
            {visibleQuestions.map((question, visibleIndex) => {
              const hasOtherCsatQuestion = visibleQuestions.some(q => 
                q.kind === 'multiple_option' && 
                q.is_csat === true && 
                q.originalIndex !== question.originalIndex
              )
              
              return (
                <QuestionForm
                  key={question.id || `new-${question.originalIndex}`}
                  question={question}
                  isAccessibilitySurvey={is_accessibility_survey}
                  questionNumber={visibleIndex + 1}
                  onQuestionChange={(updatedQuestion: Partial<QuestionType>) =>
                    handleQuestionChange(question.originalIndex, updatedQuestion)
                  }
                  onCsatChange={handleCsatChange}
                  hasOtherCsatQuestion={hasOtherCsatQuestion}
                  onDelete={() => deleteQuestion(question.originalIndex)}
                  onMoveUp={() => moveQuestion(question.originalIndex, 'up')}
                  onMoveDown={() => moveQuestion(question.originalIndex, 'down')}
                  isFirst={visibleIndex === 0}
                  isLast={visibleIndex === visibleQuestions.length - 1}
                />
              )
            })}
          </div>
          <div className="mt-6">
            <button
              type="button"
              onClick={addQuestion}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              <i className="fas fa-plus mr-2"></i>
              Agregar nueva pregunta
            </button>
          </div>
        </section>

        <div className="flex justify-end space-x-4 my-8">
          <button
            type="button"
            onClick={() => window.location.href = is_master ? '/admin/master_surveys' : '/admin/surveys'}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={processing}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Guardar Cambios y Continuar
          </button>
        </div>
      </form>
    </div>
  )
}

export default Edit
